/**
 * プロンプト入力モードの 3 タブ目「モーショングラフィックス」を出すか
 * (docs/features/motion-graphics-mode-plan.md §5.1 の本則・§10-3)。
 *
 * 表示条件 = ブランドのフラグ `features.motionGraphicsPromptMode` **かつ** backend の暫定ゲートを通る
 * (`GET /api/motion-layered/availability` が reason='restricted' でない・判定の本体は純関数 shouldShowMotionGraphicsTab)。
 * 暫定ゲート (platform admin 限定) が残っている間は他の利用者には出ない。取得中・取得失敗も出さない (fail-closed)。
 * レンダラー (video-editor) の状態では消さない (描画は生成を押した後・受付で 503 MOTION_RENDERER_UNAVAILABLE)。
 * 料金行なしはタブを出したまま、設定欄に理由を出して生成ボタンを無効にする。
 *
 * PromptModePanel (タブ)・ScenarioGenerator (トップページからの自動生成)・GenerationWorkspace
 * (2 件目のレイヤー素材の拒否)・useVideoHandlers (生成)・SVG の受け入れ (useSvgLayerUploadAllowed) で共用する。
 * react-router に依存しないので Router の外 (App.tsx で RouterProvider の兄弟の RightPanelWrapper) でも使える
 * (QueryClientProvider の中であればよい)。
 */
import { brandConfig } from '../../config/brands';
import { useUser } from '../auth/useUser';
import { useMotionLayeredAvailability, type MotionLayeredAvailability } from './availability';
import { shouldShowMotionGraphicsTab } from './layeredPromptMode';

export type MotionGraphicsTabState = {
  visible: boolean;
  /** availability を取りに行っていて結果がまだ無い (この間は motion を reference へ寄せない) */
  pending: boolean;
  /** 取りに行かない (フラグ無し・未ログイン) ときは null */
  availability: MotionLayeredAvailability | null;
};

export function useMotionGraphicsTabState(): MotionGraphicsTabState {
  const { user, isAuthenticated } = useUser();
  const flag = brandConfig.features.motionGraphicsPromptMode === true;
  // 未ログイン (usodeki の公開ホーム等) では取りに行かない (401 でトークン破棄の経路に入れない)
  const { availability, pending } = useMotionLayeredAvailability({
    enabled: flag && isAuthenticated,
    userKey: user?.user_uid ?? user?.sub ?? null,
    organizationId: user?.organization_id ?? null,
  });
  return {
    visible: shouldShowMotionGraphicsTab({ flag, availability }),
    pending,
    availability,
  };
}

export function useMotionGraphicsTabVisible(): boolean {
  return useMotionGraphicsTabState().visible;
}
